/**
 * Line-Item Gift Packaging — Web Component
 * Custom isolated file — safe to copy to any theme version
 *
 * NOTE: this file used to power a single, cart-wide "Is this a gift?"
 * toggle (element: <cart-gift-packaging>). It's been repurposed to power
 * a *per-line-item* toggle instead (element: <line-item-gift-packaging>),
 * so each product in the cart can be gift-wrapped independently. The
 * filename was kept as-is to avoid an extra theme.liquid edit — the class
 * name below reflects what it actually does now.
 *
 * - Multiple instances of this component can exist on the page at once
 *   (one per giftable line item), each fully independent.
 * - Card-only picks (My Person / SAAR) add instantly on selection.
 * - The Premium Gift Kit tier reveals a "Choose your card" grid + optional
 *   message field, with its own "Add gift packaging" confirm button.
 * - The gift line item added is tagged with a hidden `_Wrapped item key`
 *   property (set to this line item's `key`), which is how the Liquid
 *   template knows this specific line item is already wrapped and hides
 *   the toggle for it, while still showing it for other un-wrapped items.
 * - After a successful add, the whole cart drawer refreshes itself via
 *   the theme's own `cart:refresh` event.
 */

class LineItemGiftPackaging extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.switch');
    this.body = this.querySelector('.gift-packaging__body');
    this.summaryText = this.querySelector('.gift-packaging__summary-text');
    this.summaryIcon = this.querySelector('.gift-packaging__summary-icon');
    this.modal = this.querySelector('x-modal');
    this.cardGrid = this.querySelector('[data-gift-card-grid]');
    this.messageField = this.querySelector('[data-gift-message-field]');
    this.messageField?.querySelector('textarea, input')?.addEventListener('focus', (event) => {
      setTimeout(() => {
        event.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 300);
    });
    this.confirmButton = this.querySelector('[data-gift-confirm-button]');
    this.errorEl = this.querySelector('[data-gift-error]');

    this.currentOptionType = null;

    this.toggle?.addEventListener('change', () => this.onToggle());

    this.querySelectorAll('input[type="radio"][name^="line_item_gift_option_"]').forEach((radio) => {
      radio.addEventListener('change', () => this.onOptionChange(radio));
    });

    this.querySelectorAll('input[type="radio"][name^="line_item_gift_card_design_"]').forEach((radio) => {
      radio.addEventListener('change', () => this.onCardDesignChange());
    });

    this.confirmButton?.addEventListener('click', () => this.onConfirm());

    this.updateCardSelectionClasses();
  }

  onToggle() {
    const isOn = this.toggle.checked;
    if (this.body) this.body.hidden = !isOn;
    if (!isOn) {
      this.reset();
      return;
    }
    this.modal?.show?.();
  }

  onOptionChange(radio) {
    const requiresCard = radio.dataset.requiresCard === 'true';
    this.currentOptionType = radio.value.split(':')[0];

    if (this.cardGrid) this.cardGrid.hidden = !requiresCard;
    if (this.messageField) this.messageField.hidden = !requiresCard;
    if (this.confirmButton) this.confirmButton.hidden = !requiresCard;

    this.querySelectorAll('input[type="radio"][name^="line_item_gift_card_design_"]').forEach((input) => {
      input.checked = false;
    });

    if (requiresCard) {
      // Default to the last card design in the list — see the matching
      // note in custom-gift-packaging.js for why this is positional
      // rather than matched by name.
      const allCards = [...this.querySelectorAll('.gift-packaging__card-thumb')];
      const defaultCard = allCards[allCards.length - 1];
      const defaultInput = defaultCard?.querySelector('input[type="radio"][name^="line_item_gift_card_design_"]');
      if (defaultInput) defaultInput.checked = true;
    }

    this.updateCardSelectionClasses();

    this.querySelectorAll('.gift-packaging__option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(radio));
    });

    const option = radio.closest('.gift-packaging__option');
    const title = option?.querySelector('.gift-packaging__option-title')?.textContent?.trim();
    const price = option?.querySelector('.gift-packaging__option-price')?.textContent?.trim();
    const thumbSrc = option?.querySelector('.gift-packaging__option-thumb img')?.getAttribute('src');

    if (this.summaryText && title) {
      this.summaryText.textContent = price ? `${title} — ${price}` : title;
    }
    if (this.summaryIcon && thumbSrc) {
      const img = this.summaryIcon.querySelector('img');
      if (img) img.src = thumbSrc;
    }

    this.clearError();
    this.modal?.hide?.();

    // Card-only picks have nothing further to fill in — add immediately.
    if (!requiresCard) {
      this.addGiftItem({
        variantId: radio.value.split(':')[1],
        properties: this.buildProperties(),
      });
    }
  }

  onCardDesignChange() {
    this.updateCardSelectionClasses();
    this.clearError();
  }

  updateCardSelectionClasses() {
    this.querySelectorAll('.gift-packaging__card-thumb').forEach((thumb) => {
      const input = thumb.querySelector('input[type="radio"][name^="line_item_gift_card_design_"]');
      const isSelected = !!input?.checked;
      thumb.classList.toggle('is-selected', isSelected);

      const imgWrap = thumb.querySelector('.gift-packaging__card-thumb-img');
      const label = thumb.querySelector('.gift-packaging__card-thumb-label');

      if (imgWrap) {
        imgWrap.style.position = 'relative';
        imgWrap.style.border = isSelected ? '3px solid #000' : '2px solid #ddd';
        imgWrap.style.boxShadow = isSelected ? '0 0 0 2px #000' : 'none';
        imgWrap.style.transition = 'border-color .15s ease, box-shadow .15s ease';
      }

      if (label) {
        label.style.fontWeight = isSelected ? '700' : '400';
        label.style.color = isSelected ? '#000' : '#777';
      }
    });
  }

  buildProperties(extra = {}) {
    return {
      'Gift for': this.dataset.productTitle || '',
      '_Wrapped item key': this.dataset.lineItemKey || '',
      ...extra,
    };
  }

  onConfirm() {
    if (this.currentOptionType !== 'gift_wrap') return;

    const cardInput = this.querySelector('input[type="radio"][name^="line_item_gift_card_design_"]:checked');
    if (!cardInput) {
      this.showError('Please choose a card design.');
      return;
    }

    const cardLabel = cardInput.closest('.gift-packaging__card-thumb')
      ?.querySelector('.gift-packaging__card-thumb-label')?.textContent?.trim();
    const message = this.messageField?.querySelector('textarea, input')?.value?.trim() || '';

    const extra = { 'Card design': cardLabel || '' };
    if (message) extra['Gift message'] = message;

    this.addGiftItem({
      variantId: this.dataset.giftWrapVariant,
      properties: this.buildProperties(extra),
    });
  }

  async addGiftItem({ variantId, properties }) {
    if (!variantId) {
      this.showError('This gifting option is currently unavailable.');
      return;
    }

    if (this.confirmButton) this.confirmButton.disabled = true;
    this.clearError();

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
        }),
      });

      if (!response.ok) throw new Error('Gift packaging could not be added');

      // Ask the cart drawer to refresh itself the same safe way it already
      // knows how to. Once this line item's gift line item is in the cart,
      // the Liquid template stops rendering the toggle for it specifically
      // (other un-wrapped line items keep theirs).
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      if (this.confirmButton) this.confirmButton.disabled = false;
      this.showError("We couldn't add the gift packaging. Please try again.");
    }
  }

  reset() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    this.querySelectorAll('.gift-packaging__option, .gift-packaging__card-thumb').forEach((el) => {
      el.classList.remove('is-selected');
    });
    if (this.cardGrid) this.cardGrid.hidden = true;
    if (this.messageField) this.messageField.hidden = true;
    if (this.confirmButton) this.confirmButton.hidden = true;
    if (this.summaryText) this.summaryText.textContent = this.summaryText.dataset.defaultText || 'Select gifting preference';
    this.currentOptionType = null;
    this.clearError();
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }
}

if (!window.customElements.get('line-item-gift-packaging')) {
  window.customElements.define('line-item-gift-packaging', LineItemGiftPackaging);
}